import React, { useEffect } from "react";
import {
  Popover,
  Box,
  Typography,
  IconButton,
  Divider,
  styled,
} from "@mui/material";
import {
  Close as CloseIcon,
  ArrowForward as ArrowIcon,
} from "@mui/icons-material";
import { FaArrowRight } from "react-icons/fa";
import CustomButton from "./CustomButton";

// Styled components (unchanged)
const NotifyPopover = styled(Popover)(({ theme }) => ({
  "& .MuiPopover-paper": {
    borderRadius: 12,
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    border: `1px solid ${theme.palette.divider}`,
    overflow: "hidden",
    minWidth: 240,
    maxWidth: 320,
  },
}));

const Header = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2, 2, 1.5),
  background:
    theme.palette.mode === "dark"
      ? "linear-gradient(135deg, #2a3a5a 0%, #1e2a44 100%)"
      : "linear-gradient(135deg, #f0f7ff 0%, #e6f0ff 100%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
}));

const Content = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

/**
 * Reusable Notification Popover with auto-close after 6 seconds
 */
export default function NotificationPopover({
  anchorEl,
  onClose,
  title,
  subtitle,
  children,
  onActionClick,
  actionLabel = "View Details",
  showClose = true,
  autoCloseDelay = 6000, // ← you can override this prop if needed
}) {
  const open = Boolean(anchorEl);

  useEffect(() => {
    if (!open || !onClose) return;

    const timer = setTimeout(() => {
      onClose();
    }, autoCloseDelay);

    // Cleanup: prevent closing if component unmounts earlier
    return () => clearTimeout(timer);
  }, [open, onClose, autoCloseDelay]);

  return (
    <NotifyPopover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      disableRestoreFocus
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      marginThreshold={16}
    >
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <Header>
          <Typography
            variant="subtitle1"
            fontWeight={600}
            sx={{
              color: (theme) =>
                theme.palette.mode === "dark" ? "#a8d0ff" : "#1a3c8c",
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </Typography>

          {showClose && (
            <IconButton
              size="small"
              onClick={onClose}
              sx={{ color: "text.secondary" }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Header>
        <Divider />
        <Content>
          {children
            ? children
            : subtitle && (
                typeof subtitle === "string" && /<[^>]+>/.test(subtitle) ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: actionLabel ? 2 : 0 }}
                    dangerouslySetInnerHTML={{ __html: subtitle }}
                  />
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ mb: actionLabel ? 2 : 0 }}
                  >
                    {subtitle}
                  </Typography>
                )
              )}

          {onActionClick && (
            <CustomButton
              onClick={() => {
                onActionClick();
                onClose?.(); // close immediately after action
              }}
              type="fill_default"
              icon={FaArrowRight}
              title={actionLabel}
              className="w-100 mb-2"
            >
              {actionLabel}
            </CustomButton>
          )}
        </Content>
      </Box>
    </NotifyPopover>
  );
}
